import { createSelector, createSlice } from "@reduxjs/toolkit";
import { logout } from "../common/reducers/initContext";
import { dataHookFactory, initMapFactory, initMultiMapFactory, DefaultFailureAction, DefaultSuccessAction, dataHookWithCredentialsFactory } from "../common/reducers/utilities";
import { aggregatingFetch } from "../common/hooks/networkHook";
import { ensureArray } from "../common/utils/arrayUtilities";
import { ensureInteger } from "../common/utils/generalUtils";
import { buildSearchQuery } from "../utils/SearchScreenUtils";
import { markFetched, registerEntityBasedFetch } from "../common/hooks/initHook";

const key = template => ensureInteger(template.id);

const eventTemplateSlice = createSlice({
    name: 'eventTemplates',
    initialState: {
        data: {}
    },
    reducers: {
        set: (state, action) => {
            ensureArray(action.payload.ids).forEach(id => delete state.data[id]);
            ensureArray(action.payload.data).forEach(template => state.data[key(template)] = template)
        },
        addOrUpdate: (state, action) => {
            ensureArray(action.payload).forEach(template => state.data[key(template)] = template);
        },
        remove: (state, action) => {
            ensureArray(action.payload).forEach(template => delete state.data[key(template)]);
        }
    },
    extraReducers: builder => {
        builder.addCase(logout, () => eventTemplateSlice.getInitialState());
    }
});

// #region network operations
const visualizeSuccess = (verb, action) => DefaultSuccessAction(verb, 'template_one', action);
const visualizeFailure = verb => DefaultFailureAction(verb, 'template_one');

const fetchTemplate = aggregatingFetch(
    id => network => network.get('/api/v1/template/' + encodeURIComponent(id), data => eventTemplateSlice.actions.set({ data, ids: [id] }), visualizeFailure('get')),
    ids => network => network.post('/api/v1/templateSearch', { id: Array.from(ids) }, data => eventTemplateSlice.actions.set({ data, ids }), visualizeFailure('get')),
    { primitiveKey: true }
);
export const updateTemplate = (network, template) => network.put('/api/v1/template', template, eventTemplateSlice.actions.addOrUpdate, visualizeFailure('update'));
export const createTemplate = (network, template) => network.post('/api/v1/template', template, eventTemplateSlice.actions.addOrUpdate, visualizeFailure('create'));
export const instantiateTemplate = (network, id, start, eventCount, repsPerEvent, carryForward, ownerCarryForward, owners) => network.post('/api/v1/instantiateTemplate', {
    id, start, event_count: eventCount, reps_per_event: repsPerEvent, carry_forward: carryForward, owner_carry_forward: ownerCarryForward, owners
}, visualizeSuccess('create'), visualizeFailure('create'));
export const peekCurrentOwners = (network, id, start, end) => network.post('/api/v1/getCurrentOwners', {templateid: id, start: start, end: end}, null, visualizeFailure('get'))
export const peekEventsForUpdateLineOwners = (network, id, start, end) => network.post('/api/v1/eventsForUpdateLineOwners', {templateid: id, start: start, end: end}, null, visualizeFailure('get'))
export const updateLineOwners = (network, id, start, end, owners) => network.post('/api/v1/updateLineOwners', {templateid: id, start: start, end: end, owners: owners}, visualizeSuccess('create'), visualizeFailure('create'))
// #endregion

// #region selectors
const selectTemplateData = state => state.eventTemplates.data;
const selectTemplate = id => createSelector([selectTemplateData], templates => templates[id]);
const selectTemplates = ids => createSelector([selectTemplateData], templates => ids.reduce((result, id) => { let t = templates[id]; if (t) { result.push(t); } return result; }, []));
const selectActiveTemplates = createSelector([selectTemplateData], templates => Object.values(templates).filter(t => !t.inactive));
const selectInctiveTemplates = createSelector([selectTemplateData], templates => Object.values(templates).filter(t => t.inactive))
// #endregion

const listen = {
    templateCreated: (template, { dispatch, websocket }) => {
        markFetched(websocket, 'template', key(template))
        dispatch(eventTemplateSlice.actions.addOrUpdate(template))
    },
    templateUpdated: (template, { dispatch }) => dispatch(eventTemplateSlice.actions.addOrUpdate(template)),
}
// #region utilities
export const rotationgroupForGroup = (template, group) => {
    return template ? template?.rotationgroups?.find(rg => rg.lines.some(l => l.group_replicationids.some(id => group.replicationid == id))) : null;
}
export const rotationgroupForShift = (template, shift) => {
    return template ? template?.rotationgroups?.find(rg => rg.lines.some(l => l.shift_replicationid == shift.replicationid)) : null;
}
// #endregion
export const eventTemplateAccess = new Map([
    ['fullRoles', ['template manager', 'administrator']],
    ['readonlyRoles', ['auditor', 'template auditor', 'recurring rostering manager', 'recurring rostering auditor']],
    ['unRestrictedRoles', ['administrator', 'auditor', 'template manager', 'template auditor']]
]);

const checkPermissions = (credentials, eventTemplate, requiredRoles, condition = true) => {
    return (
        credentials.hasRole(requiredRoles, { office: eventTemplate?.office, client: eventTemplate?.client, location: eventTemplate?.locationid }) &&
        condition
    );
};

export const canCreateEventTemplate = (credentials, eventTemplate) => checkPermissions(credentials, eventTemplate, eventTemplateAccess.get('fullRoles'));
export const canUpdateEventTemplate = canCreateEventTemplate;
export const canRemoveEventTemplate = canCreateEventTemplate;
export const canViewFullEventTemplate = (credentials, eventTemplate) => checkPermissions(credentials, eventTemplate, eventTemplateAccess.get('unRestrictedRoles'));
export const canViewTemplate = credentials => credentials.hasRole(Array.from(eventTemplateAccess.values()).flat())
export const canRouteTemplates = ({credentials, clientConfig}) => credentials.hasRole(Array.from(eventTemplateAccess.values()).flat()) && clientConfig.eventTemplates?.enabled

const templateInitByID = initMapFactory(fetchTemplate, listen);
registerEntityBasedFetch('template', templateInitByID);

const cleanup = { root: eventTemplateSlice.name, all: selectInctiveTemplates, remove: eventTemplateSlice.actions.remove, key };
export const [useEventTemplate, realizeEventTemplate] = dataHookWithCredentialsFactory(selectTemplate, templateInitByID, {cleanup, condition: canViewTemplate });
export const [useEventTemplates] = dataHookWithCredentialsFactory(selectTemplates, initMultiMapFactory(templateInitByID, listen), {cleanup, condition: canViewTemplate });

export const searchEventTemplates = (network, search) => {
    const data = buildSearchQuery(search);
    return network.post('/api/v1/templateSearch', data, null, visualizeFailure('update'));
}

const reducer = { name: eventTemplateSlice.name, reducer: eventTemplateSlice.reducer };
export default reducer;